import { action, observable } from "mobx";
import { PayGateLanguageCode } from "./payGateLanguageCode.enum";
import {
  CALLBACK,
  CALLBACK_REGEX,
  CALLBACK_URL,
  HOSTNAME_REGEX,
  LANGUAGE_CODE,
  ORDER_ID_REGEX,
  PARENT_REGEX,
  PORT_REGEX,
  PROTOCOL_REGEX,
  REDIRECT_AFTER_CONFIRM_URL,
  REDIRECT_URL,
  SPINNER,
} from "./constants";
import LocalizationService from "services/localizationService";
import {
  persistCallbackDestination,
  SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL,
} from "utils/externalServiceUtils";
import PaymentRepository from "../../repositories/paymentRepository";
import ChargeOrderRepository from "../../repositories/chargeOrderRepository";
import GooglePayMetadata from "../../models/googlePayMetadata";
import EventBus, {
  getGooglePayMetadataFailure,
  getGooglePayMetadataSuccess,
  googlePayPayment3DSecureLoaded,
  googlePayPaymentBrowserFingerprintLoaded,
  paymentFailed,
  submitGooglePayPaymentBrowserFingerprint,
  submitGooglePayPaymentDeferred,
  submitGooglePayPaymentFailure,
  submitGooglePayPaymentSuccess,
} from "../eventBus";
import EshopError from "../../models/eshopError";
import { PayGateCommonPaymentStatus } from "../../models/googlePayInitPaymentResponse";
import { PayGatePaymentStatus } from "../../models/payGatePaymentResponse";
import GooglePayProcessPaymentRequest from "models/googlePayProcessPaymentRequest";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import { NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED, NOTIFICATION_SERVICE_START_FAILURE } from "repositories/order/constants";

const PAYMENT_GATE_CALLBACK_DESTINATION_PATH = "%PARENT%/status/%ORDER_ID%";

export default class PaymentService {
  @observable isFetchingGooglePayMetadata = false;
  @observable submittingGooglePayment = false;
  googlePayPoolingInterval: number;

  private readonly GOOGLE_PAY_STATUS_POLL_INTERVAL_MS = 5000;
  private readonly GOOGLE_PAY_STATUS_POLL_START_DELAY_MS = 20000;
  private googlePayStatusPollTimeoutId?: any;
  private googlePayStatusPollIntervalId?: any;
  private googlePayStatusPollInFlight = false;

  constructor(
    private paymentRepository: PaymentRepository,
    private notificationService: SignalRNotificationService,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private chargeOrderRepository: ChargeOrderRepository
  ) {}

  private stopGooglePayStatusPoll() {
    if (this.googlePayStatusPollTimeoutId !== undefined) {
      clearTimeout(this.googlePayStatusPollTimeoutId);
      this.googlePayStatusPollTimeoutId = undefined;
    }
    if (this.googlePayStatusPollIntervalId !== undefined) {
      clearInterval(this.googlePayStatusPollIntervalId);
      this.googlePayStatusPollIntervalId = undefined;
    }
  }

  private startGooglePayStatusPoll(orderId: string) {
    this.stopGooglePayStatusPoll();
    const checkStatus = async () => {
      if (this.googlePayStatusPollInFlight) return;
      this.googlePayStatusPollInFlight = true;
      try {
        const response = await this.chargeOrderRepository.getPayGatePayment(orderId);
        if (!response || !this.submittingGooglePayment) return;
        if (this.mapPayGateStatus(response.status) === PayGateCommonPaymentStatus.SUCCEEDED) {
          await this.finalizeGooglePayPayment(PayGateCommonPaymentStatus.SUCCEEDED);
        }
      } catch {
      } finally {
        this.googlePayStatusPollInFlight = false;
      }
    };
    this.googlePayStatusPollTimeoutId = setTimeout(() => {
      this.googlePayStatusPollTimeoutId = undefined;
      checkStatus();
      this.googlePayStatusPollIntervalId = setInterval(checkStatus, this.GOOGLE_PAY_STATUS_POLL_INTERVAL_MS);
    }, this.GOOGLE_PAY_STATUS_POLL_START_DELAY_MS);
  }

  private mapPayGateStatus(status: PayGatePaymentStatus | undefined): PayGateCommonPaymentStatus | undefined {
    switch (status) {
      case PayGatePaymentStatus.SUCCEEDED:
        return PayGateCommonPaymentStatus.SUCCEEDED;
      case PayGatePaymentStatus.DEFERRED:
        return PayGateCommonPaymentStatus.DEFERRED;
      case PayGatePaymentStatus.CANCELED:
        return PayGateCommonPaymentStatus.CANCELED;
      case PayGatePaymentStatus.FAILED:
        return PayGateCommonPaymentStatus.FAILED;
      case PayGatePaymentStatus.IN_PROGRESS:
        return PayGateCommonPaymentStatus.IN_PROGRESS;
      default:
        return undefined;
    }
  }

  @action.bound
  async getGooglePayMetadata() {
    if (this.isFetchingGooglePayMetadata) {
      return;
    }

    this.isFetchingGooglePayMetadata = true;

    const response = await this.paymentRepository.getGooglePayMetadata();

    this.isFetchingGooglePayMetadata = false;

    if (response instanceof GooglePayMetadata) {
      this.eventBus.publish(getGooglePayMetadataSuccess({ googlePayMetadata: response }));
    } else {
      this.eventBus.publish(getGooglePayMetadataFailure());
    }
  }

  @action.bound
  async submitGooglePayment(orderId: string, payload: google.payments.api.PaymentData) {
    if (this.submittingGooglePayment) {
      this.eventBus.publish(submitGooglePayPaymentFailure());
      return;
    }

    this.submittingGooglePayment = true;

    const redirectAfterConfirmationUrl = this.buildSpinnerUrl();
    const response = await this.paymentRepository.submitGooglePayment(orderId, redirectAfterConfirmationUrl, payload);

    if (response instanceof EshopError) {
      this.submittingGooglePayment = false;
      this.eventBus.publish(submitGooglePayPaymentFailure());
      return;
    }

    if (response.paymentStatus != PayGateCommonPaymentStatus.IN_PROGRESS) {
      this.finalizeGooglePayPayment(response.paymentStatus);
      return;
    }

    if (response.browserFingerprintActionUrl) {
      this.eventBus.publish(submitGooglePayPaymentBrowserFingerprint({ url: response.browserFingerprintActionUrl }));
    } else {
      this.eventBus.publish(googlePayPaymentBrowserFingerprintLoaded());
    }
  }

  @action.bound
  confirmGooglePayPayment(orderId: string) {
    return this.notificationService
      .initialize()
      .then(() => this.notificationService.serviceStart())
      .then(async () => {
        if (!this.notificationService.connectionId) {
          this.submittingGooglePayment = false;
          this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED }));
          return;
        }

        this.notificationService.subscribeToGetGooglePayStatus();

        const request = {
          language: window.navigator.language,
          javaScriptEnabled: true,
          javaEnabled: true,
          colorDepth: screen.colorDepth,
          screenWidth: Math.min(screen.width, 868),
          screenHeight: Math.min(screen.height, 500),
          timezoneOffset: new Date().getTimezoneOffset(),
        } as GooglePayProcessPaymentRequest;

        const response = await this.paymentRepository.submitGooglePayPaymentConfirmation(
          orderId,
          this.notificationService.connectionId,
          request
        );

        if (response instanceof EshopError) {
          this.submittingGooglePayment = false;
          await this.notificationService.serviceStop().catch(() => {
            // do nothing.
          });
          this.eventBus.publish(submitGooglePayPaymentFailure());
          return;
        }

        if (response.action?.browserChallenge?.challengeUrl) {
          this.eventBus.publish(googlePayPayment3DSecureLoaded({ url: response.action.browserChallenge.challengeUrl }));
        }

        this.startGooglePayStatusPoll(orderId);
      })
      .catch(() => {
        this.submittingGooglePayment = false;
        this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_START_FAILURE }));
      });
  }

  @action.bound
  async finalizeGooglePayPayment(status: PayGateCommonPaymentStatus) {
    clearInterval(this.googlePayPoolingInterval);
    this.stopGooglePayStatusPoll();
    this.submittingGooglePayment = false;

    // publish the result event immediately.
    switch (status) {
      case PayGateCommonPaymentStatus.SUCCEEDED:
        this.eventBus.publish(submitGooglePayPaymentSuccess());
        break;
      case PayGateCommonPaymentStatus.DEFERRED:
        this.eventBus.publish(submitGooglePayPaymentDeferred());
        break;
      default:
        this.eventBus.publish(submitGooglePayPaymentFailure());
    }

    await this.notificationService.serviceStop().catch(() => {
      // do nothing.
    });
  }

  @action.bound
  async payGateRedirect(orderId: string, parent: string) {
    persistCallbackDestination(
      PAYMENT_GATE_CALLBACK_DESTINATION_PATH.replace(PARENT_REGEX, parent).replace(ORDER_ID_REGEX, orderId)
    );
    window.location.href = await this.buildRedirectUrl(orderId);
  }

  public resetRedirect() {
    const redirect = sessionStorage && sessionStorage[SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL];
    if (redirect) {
      sessionStorage.removeItem(SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL);
    }
  }

  private buildRedirectUrl = async (orderId: string) => {
    const { paymentGateway } = await import("config");

    const url = new URL(REDIRECT_URL.replace(ORDER_ID_REGEX, orderId), paymentGateway);
    url.searchParams.append(LANGUAGE_CODE, this.localizedPayGateLanguageCode());
    url.searchParams.append(REDIRECT_AFTER_CONFIRM_URL, this.buildCallbackUrl(orderId));
    return url.toString();
  };

  private buildCallbackUrl = (orderId: string) =>
    CALLBACK_URL.replace(PROTOCOL_REGEX, window.location.protocol)
      .replace(HOSTNAME_REGEX, window.location.hostname)
      .replace(PORT_REGEX, window.location.port ? ":" + window.location.port : "")
      .replace(CALLBACK_REGEX, CALLBACK.replace(ORDER_ID_REGEX, orderId));

  // The spinner URL is used in the 3D secure popup of Google Pay right after confirming the 3D secure code.
  // This is used to signal that we are processing the payment. Usually it is shown only for 1-2 seconds max.
  private buildSpinnerUrl = () =>
    CALLBACK_URL.replace(PROTOCOL_REGEX, window.location.protocol)
      .replace(HOSTNAME_REGEX, window.location.hostname)
      .replace(PORT_REGEX, window.location.port ? ":" + window.location.port : "")
      .replace(CALLBACK_REGEX, SPINNER);

  private localizedPayGateLanguageCode() {
    switch (this.localizationService.locale) {
      case "cs":
        return PayGateLanguageCode.CS;
      case "en-GB":
      case "en":
        return PayGateLanguageCode.EN;
      case "de":
        return PayGateLanguageCode.DE;
      case "hu":
        return PayGateLanguageCode.HU;
      case "pl":
        return PayGateLanguageCode.PL;
      case "ru":
        return PayGateLanguageCode.RU;
      default:
        return PayGateLanguageCode.EN;
    }
  }
}
